import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, token } from '@/api/client'
import { Badge, Card } from '@/ui/primitives'

type DocEdocs = {
  id: string; title: string; type: string; lifecycle: string
  version: string; reviewStatus?: string | null; updatedAt: string
}
type Lista = { configurat: boolean; documents?: DocEdocs[]; total?: number; ascunse?: number }
type Semnatar = { nume: string | null; email: string | null; stare: string }
type Stare = { configurat: boolean; motiv?: string; baza?: string; feluri?: Record<string, string> }

const zi = (s: string) => new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: '2-digit' })

/** Panoul de documente din eDocs, pentru o rezervare / partener / factura.
 *  Documentele NU se copiaza in EventTour — sunt in arhiva, aici se vede ce e legat. */
export function DocumenteEdocs({ tip, id }: { tip: 'reservation' | 'partner' | 'invoice'; id: string | number }) {
  const qc = useQueryClient()
  const camp = useRef<HTMLInputElement>(null)
  const [fel, setFel] = useState('act_identitate')
  const [eroare, setEroare] = useState<string | null>(null)
  const [urca, setUrca] = useState(false)
  const [laSemnat, setLaSemnat] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [nume, setNume] = useState('')
  const [trimisLa, setTrimisLa] = useState<string | null>(null)

  const stare = useQuery({
    queryKey: ['edocs-stare'], queryFn: () => api.get<Stare>('/edocs/stare'), staleTime: 5 * 60_000,
  })
  const { data } = useQuery({
    queryKey: ['edocs-doc', tip, String(id)],
    queryFn: () => api.get<Lista>('/edocs/documente', { tip_entitate: tip, id_entitate: String(id) }),
    enabled: Boolean(stare.data?.configurat),
  })

  const trimite = useMutation({
    mutationFn: async (fisier: File) => {
      const fd = new FormData()
      fd.append('tip_entitate', tip); fd.append('id_entitate', String(id))
      fd.append('fel', fel); fd.append('file', fisier)
      const t = token.get()
      const res = await fetch(`${location.origin}/api/edocs/incarca`, {
        method: 'POST', body: fd, headers: t ? { Authorization: `Bearer ${t}` } : {},
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail ?? `eroare ${res.status}`)
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['edocs-doc', tip, String(id)] }),
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut urca'),
    onSettled: () => setUrca(false),
  })

  const semnatura = useMutation({
    mutationFn: (documentId: string) =>
      api.post<{ semnatari: Semnatar[] }>('/edocs/semnatura', {
        tip_entitate: tip, id_entitate: String(id),
        document_id: documentId, email: email.trim(), nume: nume.trim(),
      }),
    onSuccess: () => {
      setTrimisLa(email.trim()); setLaSemnat(null); setEmail(''); setNume('')
      qc.invalidateQueries({ queryKey: ['edocs-doc', tip, String(id)] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut trimite la semnat'),
  })

  const feluri = stare.data?.feluri ?? {}
  const documente = data?.documents ?? []

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted">
          Documente in eDocs {documente.length > 0 && <span className="tnum">({documente.length})</span>}
        </h2>
        {stare.data?.configurat && (
          <label className="text-[13px] text-accent-ink hover:underline cursor-pointer">
            {urca ? 'se urca…' : 'incarca act'}
            <input ref={camp} type="file" className="hidden" disabled={urca}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) { setEroare(null); setUrca(true); trimite.mutate(f) }
                e.target.value = ''
              }} />
          </label>
        )}
      </div>

      <Card className="p-5 space-y-3">
        {!stare.data?.configurat ? (
          <p className="text-[13.5px] text-muted">
            Arhiva eDocs nu e legata inca de aplicatie.{' '}
            {stare.data?.motiv && <span className="block mt-1 text-[12.5px]">{stare.data.motiv}</span>}
          </p>
        ) : (
          <>
            {Object.keys(feluri).length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-muted">Ce incarc:</span>
                {Object.entries(feluri).map(([k, eticheta]) => (
                  <button key={k} onClick={() => setFel(k)}
                    className={'h-8 px-3 rounded-full text-[12.5px] border transition-colors ' +
                      (fel === k ? 'border-accent bg-accent-soft text-accent-ink'
                                 : 'border-line text-muted hover:border-line-strong')}>
                    {eticheta}
                  </button>
                ))}
              </div>
            )}

            {documente.length === 0 ? (
              <p className="text-[13.5px] text-muted">
                Niciun document inca. Ce emite aplicatia (voucher, contract, factura, program)
                ajunge aici automat; actele de identitate si contractele semnate se incarca de aici.
              </p>
            ) : (
              <table className="w-full text-[14px]">
                <tbody>
                  {documente.map((d) => (
                    <tr key={d.id} className="border-t border-line first:border-0">
                      <td className="py-2.5">
                        <a href={`${stare.data?.baza?.replace('/api/v1', '')}/documents/${d.id}`}
                          target="_blank" rel="noreferrer"
                          className="text-accent-ink hover:underline">{d.title}</a>
                        <span className="block text-[12px] text-muted">{d.type} · v{d.version}</span>
                      </td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        {d.lifecycle === 'ARCHIVED' && <Badge tone="neutral">arhivat</Badge>}
                        <button onClick={() => { setEroare(null); setLaSemnat(laSemnat === d.id ? null : d.id) }}
                          className="ml-2 text-[12.5px] text-accent-ink hover:underline">
                          {laSemnat === d.id ? 'renunta' : 'la semnat'}
                        </button>
                        <span className="ml-2 text-[12.5px] text-muted tnum">{zi(d.updatedAt)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {laSemnat && (
              <div className="rounded-[var(--radius-card)] bg-page p-4 space-y-2">
                <p className="text-[13.5px] font-medium">Trimite documentul clientului, la semnat</p>
                <p className="text-[12.5px] text-muted">
                  Primeste pe email un link personal, cere de acolo un cod si semneaza. Nu are
                  nevoie de cont. Documentul semnat se intoarce in arhiva, ca versiune imutabila.
                </p>
                <div className="flex flex-wrap gap-2">
                  <input value={nume} onChange={(e) => setNume(e.target.value)}
                    placeholder="Numele clientului"
                    className="flex-1 min-w-40 h-9 px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                    placeholder="email@client.ro"
                    className="flex-1 min-w-48 h-9 px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]" />
                  <button onClick={() => semnatura.mutate(laSemnat)}
                    disabled={semnatura.isPending || !email.includes('@') || nume.trim().length < 3}
                    className="h-9 px-3.5 rounded-[var(--radius-control)] bg-accent text-white text-[14px] disabled:opacity-40">
                    {semnatura.isPending ? 'Se trimite…' : 'Trimite la semnat'}
                  </button>
                </div>
              </div>
            )}

            {trimisLa && (
              <p className="text-[13px] text-good">
                Linkul de semnare a plecat catre {trimisLa}. Cand semneaza, documentul semnat
                apare aici ca versiune noua.
              </p>
            )}

            {(data?.ascunse ?? 0) > 0 && (
              <p className="text-[12.5px] text-muted">
                {data!.ascunse} documente exista, dar nu ai drepturi sa le vezi.
              </p>
            )}
            {eroare && <p className="text-[13px] text-bad">{eroare}</p>}
          </>
        )}
      </Card>
    </div>
  )
}
